// San Diego State University: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - SDSU's Common Data Set 2025-26 (CDS_2025-2026_updated 7-28.pdf, linked
//    from Analytic Studies and Institutional Research's Common Data Set page,
//    the SOURCES url): C1 applicants, admits and enrollees for Fall 2025 with
//    the residency table, C8 (SAT and ACT not used), C21/C22 (no early
//    decision or early action). Quirk: this CDS's C8A asks about the policy
//    "for Fall 2025"; SDSU's admissions page states the same policy for this
//    cycle.
//  - SDSU Office of Admissions (admissions.sdsu.edu): First-Year Students
//    (filing period, requirements, major preparation, test policy, housing
//    rule; it names graduates "by spring 2027", so it is this cycle's page),
//    Impacted Majors, and Local Admission Area and Priority. SDSU publishes
//    no decision date beyond "Admissions decisions are made in the spring."
//  - SDSU Financial Aid, Cost of Attendance Tables: the 2026-27 standard
//    costs for an undergraduate living in university housing, from the
//    Undergraduate California Resident and Undergraduate Non-Resident tables
//    linked there. Each table's total equals the sum of its items.
//  - College Scorecard, school id 122409 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost quirk: the CDS G1/G5 2026-2027 block does not agree with itself or the
// aid office (G1 fees $2,824 and food and housing $24,749; G5 resident food
// and housing $21,856, which is the aid office's $7,848 + $14,008), so the
// page uses the aid office's tables. The out-of-state column is the
// non-resident table, which adds nonresident tuition ($471 per unit, 15 units
// a semester) and the Out of State and International Student Fee.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'SDSU Common Data Set 2025-2026',
        url: 'https://asir.sdsu.edu/common-data-set/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'SDSU Office of Admissions, First-Year Students',
        url: 'https://admissions.sdsu.edu/first-years',
        read: '2026-10-01',
    },
    impacted: {
        label: 'SDSU Office of Admissions, Impacted Majors',
        url: 'https://admissions.sdsu.edu/impacted-majors',
        read: '2026-10-01',
    },
    local: {
        label: 'SDSU Office of Admissions, Local Admission Area and Priority',
        url: 'https://admissions.sdsu.edu/local-admissions-area',
        read: '2026-10-01',
    },
    costs: {
        label: 'SDSU Financial Aid, Cost of Attendance Tables',
        url: 'https://sacd.sdsu.edu/financial-aid/financial-aid/eligibility/cost-of-attendance/cost-of-attendance-tables',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?122409-San-Diego-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'san-diego-state-university',
    kbId: 'san_diego_state_university',
    name: 'San Diego State University',
    shortName: 'San Diego State',
    location: 'San Diego, CA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (brand.sdsu.edu); decoration only.
    branding: {
        primary: '#D41736',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/san_diego_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 95444,
        admitted: 35709,
        enrolled: 6911,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 84805, admitted: 26565 },
            { group: 'Out-of-state', applied: 9043, admitted: 8202 },
            { group: 'International', applied: 1596, admitted: 942 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'SDSU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major, although every SDSU major is impacted and you are admitted to a major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'SDSU has no early decision or early action plan. Everyone applies in the same October 1 to November 30 filing period.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You must choose a major when you apply, and every SDSU major is impacted: each has more applicants than spots. You may name an alternate major, considered if space is available. Some majors ask for more:',
        rows: [
            { school: 'Computer Science and Engineering', requirement: 'Preference goes to applicants who took science and mathematics courses and earned strong grades in them.', source: 'firstYear' },
            { school: 'Dance; Music; Musical Theatre; Television, Film and New Media (Production); Theatre Arts (Performance)', requirement: 'You must pass an audition or portfolio review and meet the general admission requirements.', source: 'firstYear' },
            { school: 'Nursing (Direct Entry)', requirement: 'A B or better in high school intermediate algebra, biology and chemistry with lab (AP or IB versions included). Preference goes to strong grades in science and mathematics, and admission is highly competitive.', source: 'firstYear' },
            { school: 'Pre-majors', requirement: 'If admitted, you are placed in the pre-major and must meet department criteria, such as a minimum GPA and prerequisite courses, to move into the major.', source: 'impacted' },
            { school: 'Local admission area', requirement: 'Graduates of high schools in San Diego County south of state route 56 and east to the Imperial County line, or in Imperial County, get priority consideration in the admission ranking.', source: 'local' },
        ],
        note: 'First-year students who graduate in spring 2027 from a high school outside SDSU\'s local admission area must live on campus for two years.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'Cal State Apply', deadline: 'November 30', decision: 'In the spring', binding: false },
        ],
        note: 'Cal State Apply opens August 1 so you can prepare your application; you submit it between October 1 and November 30. No late applications are accepted. The filing fee is $70. Decisions are made in the spring; SDSU does not publish a date. If you are admitted, you must finish your planned courses with a C- or better and keep the GPA you reported, or the offer may be withdrawn.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'In line with the CSU, SDSU no longer uses SAT or ACT scores from first-year applicants to determine admission eligibility, so scores are not considered for admission. If you are accepted, SAT or ACT scores can be used to place you in math and writing courses.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also says SDSU does not use the SAT or ACT in admission decisions, marking them not considered even if submitted; its form asks about fall 2025.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (CSU basic tuition)', amounts: [6838, 6838] },
            { item: 'Nonresident tuition', amounts: [0, 14130] },
            { item: 'Required fees (campus fees)', amounts: [2942, 2942] },
            { item: 'Out of State and International Student Fee', amounts: [0, 3000] },
            { item: 'Housing (on campus)', amounts: [14008, 14008] },
            { item: 'Food (meal plan)', amounts: [7848, 7848] },
            { item: 'Books and supplies', amounts: [1014, 1014] },
            { item: 'Transportation', amounts: [1216, 1216] },
            { item: 'Personal expenses', amounts: [2566, 2566] },
        ],
        totals: [36432, 53562],
        note: 'SDSU\'s standard cost for a full-time undergraduate living in university housing, for the 9-month academic year. The housing and meal plan figures are averages of what students actually chose.',
        source: 'costs',
        where: '2026-27 tables for California residents and nonresidents',
        netPrice: {
            average: 13243,
            byIncome: [
                { income: '$0 to $30,000', amount: 8258 },
                { income: '$30,001 to $48,000', amount: 8236 },
                { income: '$48,001 to $75,000', amount: 10413 },
                { income: '$75,001 to $110,000', amount: 13498 },
                { income: 'Over $110,000', amount: 22130 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from SDSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
